import axios from 'axios';
import {
	PageLayout,
	WidthConstrainedContainer,
	Card,
	CardBody,
	SubTitle,
	BottomAnchoredControls,
	Spinner,
	useForm,
	FormInput,
	useLangContext,
	route,
	API_ENDPOINTS,
	customToast,
	Variants,
	Text,
	Button,
	ButtonTypes,
	useAuthorisationContext,
	checkTheme,
	Themes,
	Roles,
	Permissions,
} from 'carrier-fe';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type BrochureProduct = {
	/**
	 * The key from config/brochure.php. This is the value the quantity must be
	 * submitted under: the API allowlists the posted keys against that config
	 * and forwards them to Precision as the ProductId.
	 */
	id: string | number;
	sku: string;
	name: string;
};

type ProductData = Record<string, BrochureProduct[]>;

/**
 * Shape the API may send for a category: an ordered array, or the legacy object
 * keyed by product ID.
 */
type RawProductData = Record<
	string,
	BrochureProduct[] | Record<string, BrochureProduct>
>;

/**
 * The API returns each category as an ordered array so the ordering set in
 * config/brochure.php survives. Older builds returned an object keyed by
 * product ID, which browsers enumerate in ascending numeric order and so lost
 * that ordering. Accept both shapes so this page works regardless of which API
 * version it is deployed against.
 */
const normaliseProductData = (raw?: RawProductData): ProductData => {
	const normalised: ProductData = {};

	for (const [category, products] of Object.entries(raw || {})) {
		normalised[category] = Array.isArray(products)
			? products
			: Object.values(products);
	}

	return normalised;
};

type BrochureResponse = {
	first_name: string;
	last_name: string;
	email: string;
	mobile_phone_number: string;
	name: string;
	address_line_1: string;
	address_line_2: string;
	town_city: string;
	postcode_zipcode: string;
	productData: RawProductData;
	[key: string]: any;
};

const defaultData = {
	company_name: '',
	first_name: '',
	last_name: '',
	email: '',
	mobile_phone_number: '',
	address_line_1: '',
	address_line_2: '',
	postcode_zipcode: '',
	town: '',
	delivery_notes: '',
};

function BrochureOrder() {
	// Hooks and Contexts
	const navigate = useNavigate();
	const { fields, crud } = useLangContext();
	const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();

	// Access control (admins can always access):
	//  - Toshiba:   company managers.
	//  - Viessmann: company managers and engineers.
	const canAccessBrochureOrder = (() => {
		if (!hasPermissionAccess(Permissions.SUPPORT_PLATFORM_VIEW)) {
			return false;
		}

		const isAdmin =
			hasRoleAccess(Roles.SUPER_ADMIN)
			|| hasRoleAccess(Roles.PLATFORM_MANAGER);

		if (checkTheme(Themes.Toshiba)) {
			return isAdmin || hasRoleAccess(Roles.COMPANY_MANAGER);
		}

		if (checkTheme(Themes.Viessmann)) {
			return (
				isAdmin
				|| hasRoleAccess(Roles.COMPANY_MANAGER)
				|| hasRoleAccess(Roles.ENGINEER)
			);
		}

		return true;
	})();

	// State Management
	const [loading, setLoading] = useState(true);
	const [productData, setProductData] = useState<ProductData>({});
	const [quantities, setQuantities] = useState<Record<string, string>>({});

	// useForm
	const action = async (payload: typeof defaultData) => {
		const productQuantities: Record<string, number> = {};

		for (const [productId, qty] of Object.entries(quantities)) {
			const numQty = parseInt(qty, 10);

			if (numQty > 0) {
				productQuantities[productId] = numQty;
			}
		}

		return axios.post(
			route(API_ENDPOINTS.DASHBOARD.SUPPORT.BROCHURE.POST),
			{ ...payload, ...productQuantities }
		);
	};

	const { Input, Submit, setValues, isSuccess } = useForm(action, defaultData);

	// Effects
	useEffect(() => {
		if (!canAccessBrochureOrder) {
			navigate('/support');
			return;
		}

		fetchBrochureData();
	}, []);

	useEffect(() => {
		if (isSuccess) {
			navigate('/support');
		}
	}, [isSuccess]);

	const fetchBrochureData = async () => {
		try {
			const response = await axios.get<BrochureResponse>(
				route(API_ENDPOINTS.DASHBOARD.SUPPORT.BROCHURE.GET)
			);

			const data = response.data;

			setValues({
				company_name: data.name || '',
				first_name: data.first_name || '',
				last_name: data.last_name || '',
				email: data.email || '',
				mobile_phone_number: data.mobile_phone_number || '',
				address_line_1: data.address_line_1 || '',
				address_line_2: data.address_line_2 || '',
				postcode_zipcode: data.postcode_zipcode || '',
				town: data.town_city || '',
			});

			const products = normaliseProductData(data.productData);

			setProductData(products);

			// Initialise all quantities to '0'
			const initialQuantities: Record<string, string> = {};

			for (const categoryProducts of Object.values(products)) {
				for (const product of categoryProducts) {
					initialQuantities[String(product.id)] = '0';
				}
			}

			setQuantities(initialQuantities);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'An error occurred.',
				variant: Variants.Danger,
			});
		} finally {
			setLoading(false);
		}
	};

	if (!canAccessBrochureOrder) {
		return null;
	}

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center vh-100">
				<Spinner />
			</div>
		);
	}

	return (
		<PageLayout
			title={crud?.sub_titles?.brochure_order || 'Brochure Order Form'}
		>
			<WidthConstrainedContainer>
				<Text>
					{crud?.helpers?.brochure_order 
						|| `Here you can request product brochures free of charge. 
							Please select the quantity you require. 
							For larger quantities please contact your sales advisor.`
					}
				</Text>

				{Object.entries(productData).map(([category, products]) => (
					<Card key={category}>
						<CardBody>
							<SubTitle
								title={category}
								className="mb-3 w-100"
								style={{ color: '#464C5E' }}
							/>
							{products.map((product) => {
								// carrier-fe's BaseInput types `key` as a string prop
								// of its own, so the ID has to be cast.
								const productId = String(product.id);

								return (
									<FormInput
										key={productId}
										type="select"
										name={`product_${productId}`}
										label={product.name}
										value={quantities[productId] || '0'}
										options={crud?.options?.brochure_quantity || []}
										onChange={(value: any) =>
											setQuantities(prev => ({
												...prev,
												[productId]: value,
											}))
										}
									/>
								);
							})}
						</CardBody>
					</Card>
				))}

				<Card>
					<CardBody>
						<SubTitle
							title={
								`${fields?.delivery 
									|| 'delivery'
								} ${fields?.address 
									|| 'address'
								} ${crud?.sub_titles?.details 
									|| 'details'
								}`
							}
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
						/>
						{Input({ name: 'first_name', label: fields?.first_name || 'First Name' })}
						{Input({ name: 'last_name', label: fields?.last_name || 'Last Name' })}
						{Input({ name: 'company_name', label: fields?.company_name || 'Company Name' })}
						{Input({ name: 'address_line_1', label: fields?.address_line_1 || 'Address Line 1' })}
						{Input({ name: 'address_line_2', label: fields?.address_line_2 || 'Address Line 2' })}
						{Input({ name: 'postcode_zipcode', label: fields?.postcode_zipcode || 'Postcode' })}
						{Input({ name: 'town', label: fields?.town_city || 'Town / City' })}
						{Input({ name: 'mobile_phone_number', label: fields?.mobile_phone_number || 'Mobile' })}
						{Input({ name: 'email', label: fields?.email || 'Email' })}
						{Input({ name: 'delivery_notes', label: 'Delivery Notes', type: 'textarea' })}
					</CardBody>
				</Card>
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Button
					label={
						crud?.buttons.back.default 
						|| 'Back'
					}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					href='/support'
					className='me-5'
				/>
				<Submit
					defaultLabel={crud?.buttons?.place_brochure_order?.default || 'Place Brochure Order'}
					loadingLabel={crud?.buttons?.place_brochure_order?.submitting || 'Submitting...'}
				/>

			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default BrochureOrder;
